import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { CalendarBlank, Trophy, SoccerBall, Cake } from "@phosphor-icons/react";

function monthMatrix(year, month) {
  const first = new Date(year, month, 1);
  const start = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < start; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function Schedule() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [matches, setMatches] = useState([]);
  const [practices, setPractices] = useState([]);
  const [players, setPlayers] = useState([]);

  useEffect(() => {
    api.get("/matches").then((r) => setMatches(r.data));
    api.get("/practices").then((r) => setPractices(r.data));
    api.get("/players").then((r) => setPlayers(r.data));
  }, []);

  const eventsByDate = useMemo(() => {
    const m = {};
    matches.forEach((x) => { (m[x.match_date] = m[x.match_date] || []).push({ type: "match", label: `vs ${x.opponent}` }); });
    practices.forEach((x) => { (m[x.practice_date] = m[x.practice_date] || []).push({ type: "practice", label: x.title }); });
    players.forEach((p) => {
      if (!p.date_of_birth) return;
      try {
        const d = new Date(p.date_of_birth);
        const key = `${year}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        (m[key] = m[key] || []).push({ type: "birthday", label: `${p.first_name} ${p.last_name}` });
      } catch { /* ignore */ }
    });
    return m;
  }, [matches, practices, players, year]);

  const cells = monthMatrix(year, month);

  function prev() { const d = new Date(year, month - 1, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); }
  function next() { const d = new Date(year, month + 1, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); }

  return (
    <div className="space-y-6">
      <div>
        <div className="overline">Calendar</div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Schedule</h1>
      </div>

      <div className="bg-[#121212] border border-neutral-800 rounded-lg overflow-hidden">
        <div className="flex justify-between items-center p-4 border-b border-neutral-800">
          <button onClick={prev} className="h-10 px-4 rounded-md border border-neutral-800 hover:border-neutral-700 font-bold text-sm">Prev</button>
          <div className="font-display font-black text-2xl uppercase tracking-tight">{MONTHS[month]} {year}</div>
          <button onClick={next} className="h-10 px-4 rounded-md border border-neutral-800 hover:border-neutral-700 font-bold text-sm">Next</button>
        </div>
        <div className="grid grid-cols-7 border-b border-neutral-800 bg-[#0a0a0a]">
          {DAYS.map((d) => <div key={d} className="p-3 overline text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((c, i) => {
            if (!c) return <div key={i} className="min-h-[92px] border-r border-b border-neutral-900 bg-[#0a0a0a]/50" />;
            const key = `${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, "0")}-${String(c.getDate()).padStart(2, "0")}`;
            const events = eventsByDate[key] || [];
            const isToday = key === new Date().toISOString().slice(0, 10);
            return (
              <div key={i} className={`min-h-[92px] border-r border-b border-neutral-900 p-2 ${isToday ? "bg-[#D90429]/10" : ""}`}>
                <div className={`text-xs font-bold ${isToday ? "text-[#D90429]" : "text-neutral-300"}`}>{c.getDate()}</div>
                <div className="mt-1 space-y-1">
                  {events.slice(0, 3).map((e, j) => (
                    <div key={j} className="text-[10px] px-1.5 py-0.5 rounded truncate flex items-center gap-1"
                         style={{
                           background: e.type === "match" ? "#D90429" : e.type === "practice" ? "#06D6A0" : "#FFD166",
                           color: e.type === "match" ? "#fff" : "#050505",
                         }}>
                      {e.type === "match" ? <Trophy size={10} weight="fill" /> : e.type === "practice" ? <SoccerBall size={10} weight="fill" /> : <Cake size={10} weight="fill" />}
                      <span className="truncate">{e.label}</span>
                    </div>
                  ))}
                  {events.length > 3 && <div className="text-[10px] text-neutral-500">+{events.length - 3} more</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex gap-4 text-xs">
        <Legend color="#D90429" label="Match" icon={Trophy} />
        <Legend color="#06D6A0" label="Practice" icon={SoccerBall} />
        <Legend color="#FFD166" label="Birthday" icon={Cake} />
      </div>
    </div>
  );
}

const Legend = ({ color, label, icon: Icon }) => (
  <div className="flex items-center gap-2">
    <Icon size={14} weight="fill" color={color} />
    <span className="overline">{label}</span>
  </div>
);
